<script setup lang="ts">
import { RBtn } from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import type { CatalogueProject, CatalogueProjectBom } from '@/models/Projects';
import { CatalogueProjectBomType } from '@/models/Projects';
import { useCopyBomLink } from '@/composables/useCopyBomLink';

const { $gettext } = gettext;
const { copyBomLink } = useCopyBomLink();

const props = defineProps<{
  project: CatalogueProject;
  boms: CatalogueProjectBom[];
  selectedBomUuid?: string | null;
}>();

const emit = defineEmits<{
  (e: 'select-bom', bom: CatalogueProjectBom): void;
  (e: 'create-bom'): void;
  (e: 'edit-bom', bom: CatalogueProjectBom): void;
  (e: 'duplicate-bom', bom: CatalogueProjectBom): void;
  (e: 'delete-bom', bom: CatalogueProjectBom): void;
}>();

const onSelect = (bom: CatalogueProjectBom) => emit('select-bom', bom);
const onCreate = () => emit('create-bom');
const onEdit = (bom: CatalogueProjectBom) => emit('edit-bom', bom);
const onDuplicate = (bom: CatalogueProjectBom) => emit('duplicate-bom', bom);
const onDelete = (bom: CatalogueProjectBom) => emit('delete-bom', bom);

const handleCopyBomLink = async (bom: CatalogueProjectBom) => {
  await copyBomLink(props.project.uuid, bom.uuid);
};

const displayNameFor = (uuid?: string | null) => {
  if (!uuid) {
    return '';
  }
  const found = (props.boms || []).find((b) => b.uuid === uuid);
  return found?.name || `${uuid.slice(0, 8)}…`;
};

const getVersionDepth = (bom: CatalogueProjectBom): number => {
  let depth = 1;
  let current = bom;
  while (current.previousVersionUuid) {
    const prev = props.boms.find((b) => b.uuid === current.previousVersionUuid);
    if (!prev) {
      break;
    }
    depth++;
    current = prev;
  }
  return depth;
};
</script>

<template>
  <div class="d-flex align-center mt-2">
    <v-spacer />
    <r-btn
      variant="outlined"
      size="x-small"
      icon="mdi-plus"
      @click.stop="onCreate"
    >
      {{ $gettext('Create BOM') }}
    </r-btn>
  </div>

  <div
    v-if="!(boms || []).length"
    class="text-medium-emphasis text-center font-italic mt-6"
  >
    {{ $gettext('No BOMs in this project') }}
  </div>

  <v-list v-else density="compact">
    <v-list-item
      v-for="bom in boms || []"
      :key="bom.uuid"
      :active="selectedBomUuid === bom.uuid"
      @click="onSelect(bom)"
    >
      <v-list-item-title>
        {{ bom.name }}
        <v-chip
          :color="
            bom.bomType === CatalogueProjectBomType.ebom ? 'blue' : 'green'
          "
          size="x-small"
          class="mr-2"
          >{{ bom.bomType.toUpperCase() }}</v-chip
        >
        <v-tooltip
          v-if="bom.parentUuid"
          :text="
            $gettext('Branched from') + ' ' + displayNameFor(bom.parentUuid)
          "
        >
          <template #activator="{ props: tip }">
            <v-icon
              v-bind="tip"
              icon="mdi-source-branch"
              size="16"
              class="ml-1 text-medium-emphasis"
            />
          </template>
        </v-tooltip>
        <v-tooltip
          v-if="bom.previousVersionUuid"
          :text="
            $gettext('Version of') +
            ' ' +
            displayNameFor(bom.previousVersionUuid) +
            ' (v' +
            getVersionDepth(bom) +
            ')'
          "
        >
          <template #activator="{ props: tip }">
            <v-icon
              v-bind="tip"
              icon="mdi-tag-multiple-outline"
              size="16"
              class="ml-1 text-medium-emphasis"
            />
          </template>
        </v-tooltip>
      </v-list-item-title>
      <template #append>
        <div class="d-flex align-center">
          <v-btn
            icon="mdi-link-variant"
            size="small"
            variant="text"
            :title="$gettext('Copy link')"
            @click.stop="handleCopyBomLink(bom)"
          />
          <v-btn
            v-if="bom.canEdit"
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click.stop="onEdit(bom)"
          />
          <v-btn
            icon="mdi-content-copy"
            size="small"
            variant="text"
            @click.stop="onDuplicate(bom)"
          />
          <v-btn
            v-if="bom.canEdit"
            icon="mdi-delete"
            size="small"
            color="error"
            variant="text"
            @click.stop="onDelete(bom)"
          />
        </div>
      </template>
    </v-list-item>
  </v-list>
</template>

<style scoped></style>
